import axios, { AxiosResponse, AxiosError } from 'axios';
import { API_BASE_URL } from '../constants';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 45000, // Increased to 45 seconds for eSCOTT API calls (can take up to 30s)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    
    // Always add API key in development mode for direct API access
    if (process.env.NODE_ENV === 'development') {
      config.headers['X-API-Key'] = 'dev-api-key-frontend-mock';
      console.log('🔑 Adding mock API key for development');
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    // Handle network errors and timeouts
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        console.warn('Request timeout - eSCOTT API may be slow, falling back to simulation:', error.message);
      } else {
        console.warn('Network error - falling back to mock data:', error.message);
      }
    }
    
    // Handle server errors
    if (error.response?.status && error.response.status >= 500) {
      const errorData = error.response.data as { message?: string } | undefined;
      console.error('Server error:', errorData?.message || error.message);
    }
    
    // Handle client errors (400-499)
    if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
      const errorData = error.response.data as { message?: string } | undefined;
      console.warn('Client error:', errorData?.message || error.message);
    }
    
    return Promise.reject(error);
  }
);

export default api;